import { Box, Snackbar } from "@material-ui/core"
import { useEffect, useMemo, useState } from "react"

import { getTokenRacerId, isLoggedIn } from "../../utils/isLoggedIn"
import { LogInOrSignUpIndependent } from "../modal/LogInOrSignUpIndependent";
import { FilledStarIcon, OutlinedStarIcon } from "../shared/icons"

import { RemoveConfirmationDialog } from "./RemoveConfirmationDialog";

type FavoriteButtonProps = {
  eventId: number
  isMobile: boolean
}

const FavoriteButton = ({
  eventId,
  isMobile,
}: FavoriteButtonProps) => {
  const localStorageAnonFavoritesKey = 'favorite-events-anon'

  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false)
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
  const [snackbarOptions, setSnackbarOptions] = useState({
    visible: false,
    message: '',
  })

  const racerId = useMemo(() => {
    const isLogged = isLoggedIn()

    if (isLogged) {
      return getTokenRacerId()
    }

    return null
  }, [])

  const localStorageFavoritesKey = useMemo(() => {
    if (!racerId) {
      return null
    }

    return `favorite-events-${racerId}`
  }, [racerId])

  const handleSnackbarOptions = (action: 'added' | 'removed' | 'closed') => {
    switch (action) {
      case 'added':
        setSnackbarOptions({
          visible: true,
          message: 'Event added to favorites',
        })
        break
      case 'removed':
        setSnackbarOptions({
          visible: true,
          message: 'Event removed from favorites',
        })
        break
      case 'closed':
        setSnackbarOptions((prev) => ({ ...prev, visible: false }))
        break
    }
  }

  const getFavoriteEvents = (): number[] => {
    const favoriteEvents = localStorage.getItem(localStorageFavoritesKey || '')

    if (!favoriteEvents) {
      return []
    }

    const parsedFavoriteEvents = JSON.parse(favoriteEvents)
    return parsedFavoriteEvents
  }

  const isEventInFavorites = (eventId: number) => {
    const favoriteEvents = getFavoriteEvents()

    return favoriteEvents.includes(eventId)
  }

  const toggleFavorite = () => {
    if (!localStorageFavoritesKey) {
      return
    }

    const favoriteEvents = getFavoriteEvents()

    if (isEventInFavorites(eventId)) {
      const updatedFavoriteEvents = favoriteEvents.filter((favoriteEventId: number) => favoriteEventId !== eventId)
      localStorage.setItem(localStorageFavoritesKey, JSON.stringify(updatedFavoriteEvents))
      handleSnackbarOptions('removed')

      return
    }

    localStorage.setItem(localStorageFavoritesKey, JSON.stringify([...favoriteEvents, eventId]))
    handleSnackbarOptions('added')
  }

  const saveFavoriteEventFromAnonSession = () => {
    localStorage.setItem(localStorageAnonFavoritesKey, JSON.stringify(eventId))
  }

  const removeFavoriteEventFromAnonSession = () => {
    localStorage.removeItem(localStorageAnonFavoritesKey)
  }

  const onCloseAuthDialog = (reason?: string) => {
    if (reason === 'dismiss') {
      removeFavoriteEventFromAnonSession()
    }

    setIsAuthDialogOpen(false)
  }

  const handleFavoriteClick = () => {
    if (!racerId) {
      saveFavoriteEventFromAnonSession()
      setIsAuthDialogOpen(true)

      return
    }

    if (isEventInFavorites(eventId)) {
      setIsConfirmationDialogOpen(true)
    } else {
      toggleFavorite()
    }
  }

  const onSubmitRemoveFavorite = () => {
    toggleFavorite()
    setIsConfirmationDialogOpen(false)
  }

  useEffect(() => {
    const syncLastFavoriteEvent = () => {
      const event = localStorage.getItem(localStorageAnonFavoritesKey)
  
      if (racerId && event === eventId?.toString()) {
        const favoriteEvents = getFavoriteEvents()
  
        if (!favoriteEvents.includes(eventId)) {
          toggleFavorite()
        }
      }
  
      removeFavoriteEventFromAnonSession()
    }

    syncLastFavoriteEvent()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Box marginRight='8px' onClick={handleFavoriteClick} style={{ cursor: 'pointer' }}>
        {getFavoriteEvents().includes(eventId) ? (
          <FilledStarIcon />
        ) : (
          <OutlinedStarIcon />
        )}
      </Box>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={snackbarOptions.visible}
        autoHideDuration={2500}
        onClose={() => handleSnackbarOptions('closed')}
        message={snackbarOptions.message}
      />

      <RemoveConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClickClose={() => setIsConfirmationDialogOpen(false)}
        onClickSubmit={onSubmitRemoveFavorite}
      />

      <LogInOrSignUpIndependent
        firstSlideType='add-to-favorites'
        isMobile={isMobile}
        open={isAuthDialogOpen}
        closeAction={onCloseAuthDialog}
      />
    </>
  )
}

export { FavoriteButton }